/* TimeSimulatorModal — first-person interactive simulator for a historical clip.
 * Scene-by-scene narration with 3-4 choices. New AI image only on pivotal scenes.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { X, Loader2, Rocket, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TimeSimulatorModal({ clipId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [scene, setScene] = useState(null);   // current scene object from backend
  const [history, setHistory] = useState([]); // [{narration, choice}]
  const [clip, setClip] = useState(null);
  const [err, setErr] = useState(null);
  // Image stack: scene_index → image_b64. Most recent wins. Falls back to clip cover.
  const [imageOverride, setImageOverride] = useState(null);
  const scrollRef = useRef(null);

  const startSim = async () => {
    setLoading(true);
    setErr(null);
    setHistory([]);
    setImageOverride(null);
    try {
      const r = await axios.post(`${API}/timefeed/clip/${clipId}/sim/start`, {}, {
        withCredentials: true, timeout: 60000,
      });
      setScene(r.data);
      setClip(r.data.clip);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Kunde inte starta simulatorn');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    startSim();
  }, [clipId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [scene?.scene_index]);

  const choose = async (choiceText) => {
    if (!scene || busy) return;
    if (scene.is_final) { onClose(); return; }
    setBusy(true);
    const prevHist = [...history, { narration: scene.narration, choice: choiceText, role_label: scene.role_label, is_pivotal: !!scene.is_pivotal }];
    setHistory(prevHist);
    try {
      const r = await axios.post(`${API}/timefeed/clip/${clipId}/sim/step`, {
        history: prevHist.slice(-6).map((h) => ({ narration: h.narration, choice: h.choice })),
        choice: choiceText,
        scene_index: scene.scene_index || 0,
      }, { withCredentials: true, timeout: 90000 });
      setScene(r.data);
      if (r.data.image_base64) {
        setImageOverride({ b64: r.data.image_base64, mime: r.data.image_mime || 'image/png' });
      }
      // Auto-save to history book when finale arrives
      if (r.data.is_final) {
        const fullScenes = [
          ...prevHist,
          { narration: r.data.narration, choice: null, role_label: r.data.role_label, is_pivotal: !!r.data.is_pivotal },
        ];
        try {
          await axios.post(`${API}/timefeed/journey/save`, {
            clip_key: clipId,
            scenes: fullScenes,
            final_narration: r.data.narration,
          }, { withCredentials: true });
          toast.success('📜 Resan sparad i din historiebok 💜', { duration: 4000 });
        } catch {/* silent */}
      }
    } catch (e) {
      toast.error('Scenen frös 💔 ' + (e?.response?.data?.detail || ''));
    } finally {
      setBusy(false);
    }
  };

  const bgUrl = imageOverride
    ? `url("data:${imageOverride.mime};base64,${imageOverride.b64}")`
    : (scene?.image_base64
      ? `url("data:${scene.image_mime || 'image/png'};base64,${scene.image_base64}")`
      : 'linear-gradient(135deg,#3D1A78,#7C5FCC,#D06892)');

  return (
    <div
      className="fixed inset-0 z-[365] bg-black/90 backdrop-blur-md flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="sim-modal"
    >
      <div
        className="relative w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl border-2 border-violet-400/40 max-h-[94vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundImage: bgUrl, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/80 pointer-events-none" />

        {/* Header */}
        <header className="relative z-10 px-4 py-3 bg-gradient-to-r from-violet-900/90 to-black/80 backdrop-blur flex items-center justify-between text-white border-b border-violet-400/40">
          <div className="flex items-center gap-2 min-w-0">
            <Rocket className="w-4 h-4 text-violet-300 shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-violet-300 font-bold">
                Första-person-simulator
                {scene && !loading && (
                  <span className="ml-2 opacity-70">· Scen {(scene.scene_index || 0) + 1}{scene.is_final ? ' (final)' : ''}</span>
                )}
              </p>
              <h2 className="font-black text-sm sm:text-base leading-tight truncate" data-testid="sim-title">
                {clip?.hook || 'Tidsresa…'}
              </h2>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/15" data-testid="sim-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Body */}
        <div ref={scrollRef} className="relative z-10 flex-1 overflow-y-auto px-4 py-4 sm:py-6 space-y-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-white">
              <Loader2 className="w-10 h-10 animate-spin text-violet-300" />
              <p className="text-sm italic text-violet-100">Stiger in i {clip?.year || 'historien'}…</p>
              <p className="text-[10px] text-white/60">AI ritar din öppningsscen 🎬</p>
            </div>
          )}

          {err && !loading && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-center text-white">
              <p className="text-sm">💔 {err}</p>
              <div className="flex gap-2">
                <button onClick={startSim} className="px-3 py-1.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-xs font-bold">Försök igen</button>
                <button onClick={onClose} className="px-3 py-1.5 rounded-xl bg-white/20 hover:bg-white/30 text-xs font-bold">Stäng</button>
              </div>
            </div>
          )}

          {!loading && !err && scene && (
            <>
              {scene.role_label && (
                <p className="text-[10px] uppercase tracking-[0.25em] font-extrabold text-violet-300 text-center" data-testid="sim-role">
                  📍 {scene.role_label}
                </p>
              )}
              <div className="rounded-2xl bg-black/60 backdrop-blur px-4 sm:px-5 py-4 border border-violet-400/30 text-white">
                <p
                  className="text-[15px] sm:text-base leading-relaxed font-serif"
                  data-testid="sim-narration"
                >
                  {scene.narration}
                </p>
              </div>

              {/* Choices or end-state */}
              {!scene.is_final && (
                <div className="space-y-2" data-testid="sim-choices">
                  <p className="text-[10px] uppercase tracking-widest text-violet-300 font-bold px-1">
                    Vad gör du?
                  </p>
                  {(scene.choices || []).map((c, idx) => (
                    <button
                      key={idx}
                      onClick={() => choose(c)}
                      disabled={busy}
                      data-testid={`sim-choice-${idx}`}
                      className="w-full text-left px-4 py-3 rounded-2xl bg-white/90 hover:bg-white border-2 border-violet-300 hover:border-violet-500 shadow font-bold text-sm text-violet-900 transition disabled:opacity-50 flex items-center gap-2.5"
                    >
                      <span className="w-6 h-6 rounded-full bg-violet-600 text-white text-xs flex items-center justify-center shrink-0 font-black">{idx + 1}</span>
                      <span className="flex-1">{c}</span>
                    </button>
                  ))}
                  {busy && (
                    <div className="flex items-center justify-center gap-1.5 text-violet-200 text-xs italic pt-1" data-testid="sim-busy">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Tiden rullar vidare…
                    </div>
                  )}
                </div>
              )}

              {scene.is_final && (
                <div className="space-y-2" data-testid="sim-final">
                  <div className="rounded-2xl bg-gradient-to-r from-violet-700/80 to-rose-700/80 backdrop-blur px-4 py-3 border border-amber-300/40 text-white text-center">
                    <p className="text-[10px] uppercase tracking-widest font-extrabold text-amber-200">Slutet</p>
                    <p className="text-sm italic mt-1">Din tidsresa är över. Historien rullar vidare som den alltid gjort. 💜</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={startSim}
                      data-testid="sim-restart"
                      className="flex-1 px-3 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-bold text-sm flex items-center justify-center gap-1.5"
                    >
                      <RotateCcw className="w-4 h-4" /> Spela igen
                    </button>
                    <button
                      onClick={onClose}
                      className="flex-1 px-3 py-2.5 rounded-xl bg-white/20 hover:bg-white/30 text-white font-bold text-sm"
                    >
                      Stäng
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
